const normalizeRole = (role) =>
  typeof role === "string" ? role.toLowerCase() : role?.toLowerCase?.() || "";

export const normalizeUserRoles = (userRoles = []) =>
  (Array.isArray(userRoles) ? userRoles : []).map(normalizeRole).filter(Boolean);

export const hasRoleInList = (userRoles, allowedRoles = []) => {
  const allowed = new Set((allowedRoles || []).map(normalizeRole));
  return normalizeUserRoles(userRoles).some((role) => allowed.has(role));
};

export const hasPurchaseOrderAccess = (userRoles, roleCapabilities) =>
  hasRoleInList(userRoles, roleCapabilities?.purchase_order_access_roles || ["systemowner", "owner", "itsupport", "superuser", "generaladmin", "finance", "manager"]);

export const hasSupplierAccess = (userRoles, roleCapabilities) =>
  hasRoleInList(userRoles, roleCapabilities?.supplier_access_roles || ["systemowner", "owner", "itsupport", "superuser", "generaladmin", "finance", "manager"]);

export const hasFinanceAccess = (userRoles, roleCapabilities) =>
  hasRoleInList(userRoles, roleCapabilities?.finance_access_roles);

export const hasSystemAdminAccess = (userRoles, roleCapabilities) =>
  hasRoleInList(userRoles, roleCapabilities?.system_admin_roles);

export const hasFullPropertyAccess = (userRoles, roleCapabilities) =>
  hasRoleInList(userRoles, roleCapabilities?.full_property_access_roles);

export const hasPropertyWriteAccess = (userRoles, permissions) =>
  normalizeUserRoles(userRoles).some((role) =>
    (permissions?.[role] || []).some((method) =>
      ["PUT", "PATCH", "POST", "DELETE"].includes(method)
    )
  );

export const isGlobalRoleName = (roleName) => {
  const normalized = normalizeRole(roleName);
  return ["systemowner", "owner", "itsupport", "superuser", "generaladmin", "finance"].includes(
    normalized
  );
};

export const getRoleAssignmentsLabel = (roleName) =>
  isGlobalRoleName(roleName) ? "Global" : "Per property/unit";

export const getRolePropertyAccessLabel = (roleName, permissions, roleCapabilities) => {
  const normalized = normalizeRole(roleName);
  const fullAccessRoles = new Set(
    (roleCapabilities?.full_property_access_roles || []).map(normalizeRole)
  );

  if (!fullAccessRoles.has(normalized)) {
    return "Assigned only";
  }

  const methods = permissions?.[normalized] || [];
  const canWrite = methods.some((method) =>
    ["PUT", "PATCH", "POST", "DELETE"].includes(method)
  );
  return canWrite ? "Yes (read/write)" : "Yes (read-only)";
};

export const getRoleFinanceAccessLabel = (roleName, roleCapabilities) => {
  const normalized = normalizeRole(roleName);
  const financeRoles = new Set(
    (roleCapabilities?.finance_access_roles || []).map(normalizeRole)
  );
  return financeRoles.has(normalized) ? "Yes" : "No";
};

const ROLE_BEHAVIOUR_ORDER = [
  "systemowner",
  "itsupport",
  "generaladmin",
  "finance",
  "manager",
  "viewer",
];

export const sortRolesForBehaviourSummary = (roles = []) =>
  [...roles].sort((left, right) => {
    const leftIndex = ROLE_BEHAVIOUR_ORDER.indexOf(normalizeRole(left.name));
    const rightIndex = ROLE_BEHAVIOUR_ORDER.indexOf(normalizeRole(right.name));
    const safeLeft = leftIndex === -1 ? ROLE_BEHAVIOUR_ORDER.length : leftIndex;
    const safeRight = rightIndex === -1 ? ROLE_BEHAVIOUR_ORDER.length : rightIndex;
    if (safeLeft !== safeRight) return safeLeft - safeRight;
    return (left.display_name || left.name || "").localeCompare(right.display_name || right.name || "");
  });

export const parseRolePermissionsResponse = (data) => {
  if (!data || typeof data !== "object") {
    return {
      permissions: {},
      roleCapabilities: {
        finance_access_roles: [],
        purchase_order_access_roles: [],
        full_property_access_roles: [],
        system_admin_roles: [],
      },
    };
  }

  if (data.role_permissions) {
    return {
      permissions: data.role_permissions,
      roleCapabilities: {
        finance_access_roles: data.finance_access_roles || [],
        purchase_order_access_roles: data.purchase_order_access_roles,
        supplier_access_roles: data.supplier_access_roles,
        full_property_access_roles: data.full_property_access_roles || [],
        system_admin_roles: data.system_admin_roles || [],
      },
    };
  }

  return {
    permissions: data,
    roleCapabilities: {
      finance_access_roles: ["systemowner", "owner", "itsupport", "superuser", "finance"],
      full_property_access_roles: [
        "systemowner",
        "owner",
        "itsupport",
        "superuser",
        "generaladmin",
        "finance",
      ],
      system_admin_roles: ["systemowner", "owner", "itsupport", "superuser"],
    },
  };
};
